import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  Download, Mail, Link2, FileText, Loader2, Check,
  GraduationCap, DollarSign, Calendar, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReportGenerator({ 
  userData, 
  pathways, 
  scholarships, 
  actionPlan, 
  roadmap90Day,
  roadmap12Month,
  colleges 
}) {
  const { getAuthHeader } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');

  const generatePDF = async () => {
    setGenerating(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;

      // Helper functions
      const addNewPage = () => {
        pdf.addPage();
        yPos = margin;
      };

      const checkPageBreak = (height) => {
        if (yPos + height > pageHeight - margin) {
          addNewPage();
        }
      };

      // Header
      pdf.setFillColor(30, 58, 138); // Blue
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Agentic FuturePath AI Report', margin, 25);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, margin, 35);
      
      yPos = 55;
      pdf.setTextColor(30, 41, 59);

      // Student Info Section
      if (userData) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Student Profile', margin, yPos);
        yPos += 8;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Name: ${userData.full_name || 'N/A'}`, margin, yPos);
        yPos += 5;
        pdf.text(`Education: ${userData.education_level || 'N/A'}`, margin, yPos);
        yPos += 5;
        pdf.text(`Location: ${userData.city || ''}, ${userData.state || ''}`, margin, yPos);
        yPos += 5;
        pdf.text(`Interests: ${(userData.interests || []).join(', ') || 'N/A'}`, margin, yPos);
        yPos += 5;
        pdf.text(`Career Goals: ${userData.career_aspirations || 'Exploring options'}`, margin, yPos);
        yPos += 15;
      }

      // Top 3 Pathways
      if (pathways?.paths?.length > 0) {
        checkPageBreak(50);
        
        pdf.setFillColor(240, 249, 255);
        pdf.rect(margin - 5, yPos - 5, pageWidth - 2*margin + 10, 8, 'F');
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Top 3 Career Pathways', margin, yPos);
        yPos += 12;
        
        pdf.setTextColor(30, 41, 59);
        const maxTextWidth = pageWidth - 2*margin - 10;
        
        pathways.paths.forEach((path, index) => {
          checkPageBreak(30);
          
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          const titleLines = pdf.splitTextToSize(`${index + 1}. ${path.title} (${path.match_score}% match)`, maxTextWidth);
          pdf.text(titleLines, margin, yPos);
          yPos += titleLines.length * 5;
          
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          const infoLines = pdf.splitTextToSize(`Type: ${path.type} | Timeline: ${path.timeline} | Cost: ${path.estimated_cost}`, maxTextWidth - 5);
          pdf.text(infoLines, margin + 5, yPos);
          yPos += infoLines.length * 4;
          
          const descLines = pdf.splitTextToSize(path.description || '', maxTextWidth - 5);
          pdf.text(descLines, margin + 5, yPos);
          yPos += descLines.length * 4 + 8;
        });
        
        yPos += 5;
      }

      // Scholarships
      if (scholarships?.scholarships?.length > 0) {
        checkPageBreak(40);
        
        pdf.setFillColor(240, 253, 244);
        pdf.rect(margin - 5, yPos - 5, pageWidth - 2*margin + 10, 8, 'F');
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(22, 101, 52);
        pdf.text('Scholarship Matches', margin, yPos);
        yPos += 12;
        
        pdf.setTextColor(30, 41, 59);
        const scholarshipMaxWidth = pageWidth - 2*margin - 30;
        
        scholarships.scholarships.forEach((scholarship, index) => {
          checkPageBreak(18);
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          const nameLines = pdf.splitTextToSize(scholarship.name || '', scholarshipMaxWidth);
          pdf.text(nameLines, margin, yPos);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${scholarship.amount}`, pageWidth - margin - 25, yPos);
          yPos += nameLines.length * 4 + 2;
          
          pdf.setFontSize(9);
          const deadlineText = `Deadline: ${scholarship.deadline} | Match: ${scholarship.match_score}%`;
          pdf.text(deadlineText, margin + 5, yPos);
          yPos += 8;
        });
        
        if (scholarships.total_potential) {
          pdf.setFont('helvetica', 'bold');
          pdf.text(`Total Potential: ${scholarships.total_potential}`, margin, yPos);
          yPos += 10;
        }
      }

      // 7-Day Action Plan
      if (actionPlan?.actions?.length > 0) {
        checkPageBreak(50);
        
        pdf.setFillColor(254, 249, 195);
        pdf.rect(margin - 5, yPos - 5, pageWidth - 2*margin + 10, 8, 'F');
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(161, 98, 7);
        pdf.text('7-Day Action Plan', margin, yPos);
        yPos += 10;
        
        pdf.setFontSize(10);
        pdf.setTextColor(30, 41, 59);
        pdf.text(`Focus: ${actionPlan.week_focus || 'Personal Development'}`, margin, yPos);
        yPos += 8;
        
        actionPlan.actions.slice(0, 7).forEach((action) => {
          checkPageBreak(12);
          
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`Day ${action.day}: ${action.title}`, margin, yPos);
          yPos += 4;
          
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${action.description} (${action.time_estimate})`, margin + 5, yPos);
          yPos += 6;
        });
        
        yPos += 5;
      }

      // 90-Day Roadmap Summary
      if (roadmap90Day?.quarter_focus) {
        checkPageBreak(30);
        
        pdf.setFillColor(243, 232, 255);
        pdf.rect(margin - 5, yPos - 5, pageWidth - 2*margin + 10, 8, 'F');
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(126, 34, 206);
        pdf.text('90-Day Roadmap', margin, yPos);
        yPos += 10;
        
        pdf.setFontSize(10);
        pdf.setTextColor(30, 41, 59);
        pdf.text(`Focus: ${roadmap90Day.quarter_focus}`, margin, yPos);
        yPos += 8;
        
        if (roadmap90Day.key_milestones) {
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Key Milestones:', margin, yPos);
          yPos += 5;
          
          pdf.setFont('helvetica', 'normal');
          roadmap90Day.key_milestones.slice(0, 3).forEach((milestone) => {
            checkPageBreak(8);
            pdf.text(`• ${milestone.title} - ${milestone.target_date}`, margin + 5, yPos);
            yPos += 5;
          });
        }
        
        yPos += 5;
      }

      // College Recommendations
      if (colleges?.colleges?.length > 0) {
        checkPageBreak(40);
        
        pdf.setFillColor(255, 241, 242);
        pdf.rect(margin - 5, yPos - 5, pageWidth - 2*margin + 10, 8, 'F');
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(190, 18, 60);
        pdf.text('College Recommendations', margin, yPos);
        yPos += 12;
        
        pdf.setTextColor(30, 41, 59);
        
        colleges.colleges.slice(0, 5).forEach((college) => {
          checkPageBreak(15);
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${college.name}`, margin, yPos);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`${college.match_score}% match`, pageWidth - margin - 25, yPos);
          yPos += 5;
          
          pdf.setFontSize(9);
          pdf.text(`${college.location} | Tuition: ${college.tuition} | Acceptance: ${college.acceptance_rate}`, margin + 5, yPos);
          yPos += 8;
        });
      }

      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.text(
          `Page ${i} of ${totalPages} | Agentic FuturePath AI - Your Career Navigation Partner`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save the PDF
      pdf.save(`FuturePath_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleEmailShare = async () => {
    if (!recipientEmail) {
      // Open modal to get email
      setEmailModalOpen(true);
      return;
    }
    
    setEmailing(true);
    try {
      // Generate HTML content for email
      const reportHtml = generateReportHtml();
      
      await axios.post(`${API}/api/email/send-report`, {
        recipient_email: recipientEmail,
        subject: `Career Report for ${userData?.full_name || 'Student'}`,
        report_html: reportHtml
      }, getAuthHeader());
      
      toast.success(`Report sent to ${recipientEmail}!`);
      setEmailModalOpen(false);
      setRecipientEmail('');
    } catch (error) {
      console.error('Email error:', error);
      if (error.response?.status === 503) {
        toast.error('Email service not configured. Please download PDF instead.');
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } finally {
      setEmailing(false);
    }
  };
  
  const generateReportHtml = () => {
    let html = '';
    
    // User info
    if (userData) {
      html += `
        <h2 style="color: #3b82f6; margin-bottom: 12px;">Student Profile</h2>
        <p><strong>Name:</strong> ${userData.full_name || 'N/A'}</p>
        <p><strong>Location:</strong> ${userData.city || ''}, ${userData.state || ''}</p>
        <p><strong>Career Goals:</strong> ${userData.career_aspirations || 'Exploring options'}</p>
        <hr style="border-color: #334155; margin: 20px 0;" />
      `;
    }
    
    // Pathways
    if (pathways?.paths?.length > 0) {
      html += `<h2 style="color: #3b82f6; margin-bottom: 12px;">Top Career Pathways</h2>`;
      pathways.paths.forEach((path, i) => {
        html += `
          <div style="background: #1e293b; padding: 12px; border-radius: 8px; margin-bottom: 8px;">
            <p style="margin: 0;"><strong style="color: #60a5fa;">${i + 1}. ${path.title}</strong> <span style="color: #22c55e;">(${path.match_score}% match)</span></p>
            <p style="margin: 4px 0 0 0; color: #94a3b8; font-size: 14px;">${path.description}</p>
          </div>
        `;
      });
      html += `<hr style="border-color: #334155; margin: 20px 0;" />`;
    }
    
    // Scholarships
    if (scholarships?.scholarships?.length > 0) {
      html += `<h2 style="color: #22c55e; margin-bottom: 12px;">Scholarship Matches</h2>`;
      scholarships.scholarships.forEach((s) => {
        html += `
          <div style="display: flex; justify-content: space-between; background: #1e293b; padding: 12px; border-radius: 8px; margin-bottom: 8px;">
            <span style="color: #e2e8f0;">${s.name}</span>
            <span style="color: #22c55e; font-weight: bold;">${s.amount}</span>
          </div>
        `;
      });
      if (scholarships.total_potential) {
        html += `<p style="text-align: center; color: #22c55e; font-weight: bold;">Total Potential: ${scholarships.total_potential}</p>`;
      }
      html += `<hr style="border-color: #334155; margin: 20px 0;" />`;
    }
    
    // Action Plan
    if (actionPlan?.actions?.length > 0) {
      html += `<h2 style="color: #f59e0b; margin-bottom: 12px;">7-Day Action Plan</h2>`;
      actionPlan.actions.slice(0, 7).forEach((a) => {
        html += `
          <p style="margin: 4px 0;"><strong style="color: #fbbf24;">Day ${a.day}:</strong> <span style="color: #e2e8f0;">${a.title}</span></p>
        `;
      });
    }
    
    return html;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/app/report`);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const hasData = pathways || scholarships || actionPlan || colleges;

  if (!hasData) {
    return null;
  }

  return (
    <div className="glass-card p-4 mb-6" data-testid="report-generator">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-outfit font-semibold text-white">Your Career Report</h3>
            <p className="text-sm text-slate-300">Download, share, or email your personalized plan</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={generatePDF}
            disabled={generating}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="download-pdf-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
          
          <Button
            onClick={() => setEmailModalOpen(true)}
            disabled={emailing}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="email-share-btn"
          >
            {emailing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mail className="w-4 h-4 mr-2" />
            )}
            Email
          </Button>
          
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="copy-link-btn"
          >
            {copied ? (
              <Check className="w-4 h-4 mr-2 text-emerald-400" />
            ) : (
              <Link2 className="w-4 h-4 mr-2" />
            )}
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
        </div>
      </div>
      
      {/* Report Contents Summary */}
      <div className="mt-4 flex flex-wrap gap-3">
        {pathways?.paths?.length > 0 && (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
            <GraduationCap className="w-3 h-3" />
            {pathways.paths.length} Pathways
          </span>
        )}
        {scholarships?.scholarships?.length > 0 && (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">
            <DollarSign className="w-3 h-3" />
            {scholarships.scholarships.length} Scholarships
          </span>
        )}
        {actionPlan?.actions?.length > 0 && (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">
            <Calendar className="w-3 h-3" />
            7-Day Plan
          </span>
        )}
        {colleges?.colleges?.length > 0 && (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-rose-500/20 text-rose-400 rounded-full text-xs">
            <MapPin className="w-3 h-3" />
            {colleges.colleges.length} Colleges
          </span>
        )}
      </div>
      
      {/* Email Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-[#0a1729]/60 flex items-start sm:items-center justify-center z-[200] overflow-y-auto p-4" onClick={() => setEmailModalOpen(false)}>
          <div className="glass-card p-6 max-w-md w-full my-10 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">Send Report via Email</h3>
            <p className="text-slate-300 text-sm mb-4">Enter the recipient's email address to send your career report.</p>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="recipient@email.com"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white mb-4"
              data-testid="email-input"
            />
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setEmailModalOpen(false)}
                className="border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEmailShare}
                disabled={emailing || !recipientEmail}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {emailing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Send Report
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
